import { useMutation } from '@tanstack/react-query';
import {
  ECONOMIE_ANNUELLE_CENTIMES,
  JOURS_ESSAI_GRATUIT,
  NOM_FORMULE,
  PRIX_ANNUEL_CENTIMES,
  PRIX_DUO_ANNUEL_CENTIMES,
  PRIX_DUO_MENSUEL_CENTIMES,
  PRIX_MENSUEL_CENTIMES,
  SUPPLEMENT_DUO_ANNUEL_CENTIMES,
  SUPPLEMENT_DUO_CENTIMES,
  prixEnEuros,
} from '@repere/core';
import { useEffect, useRef, useState } from 'react';
import { useSession } from '../auth/session';
import { estNatif } from '../natif/pont';
import { useRegime } from './regime';
import { Alerte, Bouton } from '../ui';

/** Ce que l'abonnement ouvre, dit du point de vue de l'usage et non de la facturation. */
const CE_QUE_CA_OUVRE = [
  'Le journal du dossier, horodaté et inaltérable',
  'Les dépenses partagées et le calcul du solde',
  'Les documents et les fiches des enfants',
  'Les exports pour un avocat ou un médiateur',
  'La messagerie sans limite de messages',
] as const;

/**
 * Invitation à souscrire, à la place d'un refus.
 *
 * Un parent qui a rédigé une entrée de journal et qui reçoit « Cette fonctionnalité fait
 * partie de l'abonnement » lit un mur. La même information, présentée comme une porte,
 * répond à la question qu'il se pose vraiment : qu'est-ce que ça ouvre, et combien.
 *
 * Trois choses que cette fenêtre ne fait pas, et qui tiennent au produit :
 *
 * - **Elle ne dramatise pas.** Ce qui est déjà enregistré reste lisible, et c'est dit en
 *   premier. Un abonnement qui expire ferme l'écriture, jamais la consultation :
 *   l'historique appartient aux deux parents, pas au payeur (docs/06 §1).
 * - **Elle ne culpabilise pas et ne compte pas les jours.** Aucune urgence fabriquée,
 *   aucun décompte, aucune offre qui expire.
 * - **Elle rappelle ce qui restera gratuit** — le calendrier et les alertes. Le taire
 *   ferait croire que refuser coupe la coordination avec l'autre parent, ce qui est faux
 *   et irait contre l'objet même du produit.
 *
 * Elle s'ouvre dans quatre situations, et son titre le dit : à l'échec d'une écriture
 * (`refus`), au rappel périodique des formules payantes (`rappel`), à la première ouverture
 * d'un dossier (`ouverture`), et sur demande depuis l'en-tête du dossier (`demande`). Les
 * deux du milieu sont assumés pour ce qu'ils sont — des sollicitations commerciales non
 * demandées —, ce qui oblige à les titrer autrement : garder « cette partie du dossier
 * s'ouvre avec l'abonnement » aurait fait croire à un blocage venant d'être rencontré.
 * `demande`, lui, ne sollicite personne : c'est le parent qui est venu.
 */
export function InvitationAbonnement({
  motif = 'refus',
  essaiParDefaut = true,
  duoParDefaut = false,
  onFermer,
}: {
  motif?: 'refus' | 'rappel' | 'ouverture' | 'demande';
  /**
   * L'essai gratuit est-il proposé coché ?
   *
   * Vrai par défaut : des deux démarrages, c'est celui qui engage le moins — la même
   * raison qui fait présélectionner le mensuel plutôt que l'annuel. Le choix contraire
   * reste à un clic, et il est écrit en toutes lettres à côté, pas caché derrière un
   * repli. La carte est demandée dans les deux cas, ce que la fenêtre dit aussi.
   *
   * L'appelant peut le renverser : celui qui arrive par « Payer tout de suite » n'a pas à
   * décocher ce qu'il vient de refuser.
   */
  essaiParDefaut?: boolean;
  /**
   * La portée « les deux parents » est-elle cochée à l'ouverture ?
   *
   * Faux par défaut, et ce défaut n'est pas neutre : une case pré-cochée ferait payer un
   * supplément à qui n'a rien demandé. L'appelant ne le renverse que lorsque le parent
   * vient de désigner cette portée lui-même — cocher ce qu'il vient de choisir n'est plus
   * un piège, et le lui faire recocher serait lui redemander sa réponse.
   */
  duoParDefaut?: boolean;
  onFermer: () => void;
}) {
  const dialogue = useRef<HTMLDialogElement>(null);
  const { client, profil } = useSession();
  const [ferme, setFerme] = useState(false);
  // Le mensuel par défaut : c'est l'engagement le plus faible, et un produit qui vend la
  // confiance ne présélectionne pas l'option la plus chère à payer d'un coup.
  const [periodicite, setPeriodicite] = useState<'monthly' | 'yearly'>('monthly');
  /**
   * Payer aussi pour l'autre parent. Décoché sauf si le parent vient de le demander :
   * cocher d'office transformerait un geste en attente implicite, et ferait payer un
   * supplément à qui n'a rien demandé.
   */
  const [duo, setDuo] = useState(duoParDefaut);
  /** Commencer par les sept jours gratuits, ou payer tout de suite. */
  const [essai, setEssai] = useState(essaiParDefaut);

  // L'autre parent a-t-il déjà rejoint ? Cela ne décide plus si l'option s'affiche — Duo
  // est une des quatre formules, pas un supplément conditionnel — mais ce qui est écrit à
  // côté : couvrir quelqu'un qui est là et réserver une place pour quelqu'un qui n'est pas
  // encore arrivé ne se promettent pas de la même façon.
  const regime = useRegime();
  // Le doute compte comme « il y a quelqu'un » : tant que la réponse n'est pas arrivée,
  // `peutCouvrirCoparent` vaut `undefined`, et le lire comme un « non » ferait clignoter
  // « l'autre parent n'a pas encore rejoint » sur l'écran d'un parent dont le co-parent est
  // là depuis des mois. Se taire une seconde de trop ne coûte rien ; annoncer une absence
  // qui n'existe pas, si.
  const placeEnAttente = regime.data !== undefined && !regime.data.peutCouvrirCoparent;
  const portee = duo ? 'duo' : 'seul';

  const prix =
    portee === 'duo'
      ? { mensuel: PRIX_DUO_MENSUEL_CENTIMES, annuel: PRIX_DUO_ANNUEL_CENTIMES }
      : { mensuel: PRIX_MENSUEL_CENTIMES, annuel: PRIX_ANNUEL_CENTIMES };

  // Le supplément suit la périodicité choisie : « 5 € de plus par mois » posé à côté d'un
  // prix annuel serait faux d'un facteur dix, et c'est exactement le genre d'écart qu'un
  // parent découvre chez Stripe.
  const supplement =
    periodicite === 'yearly'
      ? { texte: `${prixEnEuros(SUPPLEMENT_DUO_ANNUEL_CENTIMES)} de plus par an` }
      : { texte: `${prixEnEuros(SUPPLEMENT_DUO_CENTIMES)} de plus par mois` };

  /**
   * Le paiement ne se termine pas au même endroit selon d'où l'on vient.
   *
   * **Dans le navigateur**, rien ne change : le serveur rend l'adresse de Stripe et on y
   * va. C'est un site web, il a le droit de vendre ce qu'il vend.
   *
   * **Dans l'application**, l'adresse n'est jamais rendue : elle part dans la boîte aux
   * lettres du parent, et c'est de là qu'il paie. Deux raisons, et la seconde est celle
   * qui a décidé de la forme :
   *
   * 1. Apple et Google interdisent qu'une application dirige vers un paiement qu'ils ne
   *    perçoivent pas. Un bouton qui envoie un e-mail ne dirige nulle part.
   * 2. **Le parent ne se reconnecte jamais.** L'adresse de Stripe est auto-portante : elle
   *    s'ouvre sans compte et sans session, sur l'appareil qui relève la boîte. C'est la
   *    seule forme qui évite de redemander un mot de passe au moment précis où l'on paie —
   *    un navigateur ouvert depuis la coque n'a, lui, aucune session à présenter.
   *
   * L'application n'apprend pas le paiement par ce chemin, et n'en a pas besoin : le
   * webhook signé ouvre l'abonnement, et le retour au premier plan redemande les droits.
   */
  const parEmail = estNatif();

  const souscrire = useMutation({
    mutationFn: () =>
      client.request<{ disponible: boolean; charge?: { url?: string }; envoye?: boolean }>(
        '/billing/checkout',
        {
          method: 'POST',
          body: { periodicite, portee, essai, ...(parEmail ? { destination: 'email' } : {}) },
        },
      ),
    onSuccess: (reponse) => {
      // Le serveur répond `disponible: false` quand les clés Stripe ne sont pas
      // configurées, plutôt que d'échouer en profondeur : un parcours qui tombe au
      // troisième écran est pire qu'un parcours annoncé fermé. On le relaie tel quel.
      if (!reponse.disponible) {
        setFerme(true);
        return;
      }
      // Envoyé : l'écran le dit et s'arrête là. Rien à ouvrir, rien à attendre.
      if (reponse.envoye === true) return;
      if (reponse.charge?.url) {
        globalThis.location.assign(reponse.charge.url);
        return;
      }
      setFerme(true);
    },
  });

  /** Le lien est parti : l'écran cesse de proposer, il indique où regarder. */
  const envoye = souscrire.data?.envoye === true;

  useEffect(() => {
    const element = dialogue.current;
    if (!element || element.open) return;
    // `showModal` peut manquer (jsdom) : la fenêtre reste alors un bloc ordinaire plutôt
    // que de faire échouer le rendu.
    element.showModal?.();
  }, []);

  return (
    <dialog
      className="invitation-abonnement"
      ref={dialogue}
      aria-labelledby="abonnement-titre"
      onClose={onFermer}
      onClick={(evenement) => {
        if (evenement.target === dialogue.current) dialogue.current?.close();
      }}
    >
      <div className="invitation-abonnement-contenu">
        <h2 id="abonnement-titre">
          {motif === 'refus'
            ? 'Cette partie du dossier s’ouvre avec l’abonnement'
            : 'Passer à Solo ou Duo'}
        </h2>

        <p className="invitation-abonnement-rassurance">
          Ce que vous avez déjà enregistré reste lisible, maintenant et plus tard. Un
          abonnement qui s'arrête ferme l'écriture, jamais la consultation : l'historique
          appartient aux deux parents.
        </p>

        {/* La formule que le prix affiché désigne. Deux prix qui changent sous l'effet d'un
            interrupteur, sans que rien ne les nomme, se lisent comme une remise ou une
            majoration ; nommés, ce sont deux offres de la grille publique — les mêmes mots
            que la page de tarifs, ce qui permet de retrouver l'une dans l'autre. */}
        <p className="invitation-abonnement-formule">
          <strong>{portee === 'duo' ? NOM_FORMULE.duo : NOM_FORMULE.solo}</strong>
          <span className="discret">
            {portee === 'duo' ? 'Pour vous deux' : 'Pour vous'}
          </span>
        </p>

        {/* Le prix avant la liste : c'est la deuxième question, juste après « est-ce que
            je perds ce que j'ai écrit ? ». Le cacher sous un bouton donne l'impression
            qu'il y a quelque chose à cacher. */}
        <div className="invitation-abonnement-offres" role="radiogroup" aria-label="Périodicité">
          <button
            type="button"
            role="radio"
            aria-checked={periodicite === 'monthly'}
            className={periodicite === 'monthly' ? 'actif' : undefined}
            onClick={() => setPeriodicite('monthly')}
          >
            <span className="montant">{prixEnEuros(prix.mensuel)}</span>
            <span className="cadence">par mois</span>
            <span className="detail">Sans engagement</span>
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={periodicite === 'yearly'}
            className={periodicite === 'yearly' ? 'actif' : undefined}
            onClick={() => setPeriodicite('yearly')}
          >
            <span className="montant">{prixEnEuros(prix.annuel)}</span>
            <span className="cadence">par an</span>
            {/* L'économie est dite en euros, pas en pourcentage : « deux mois offerts » se
                vérifie de tête, un « -17 % » demande de faire confiance. */}
            <span className="detail">
              Deux mois offerts,{' '}
              {prixEnEuros(portee === 'duo' ? prix.mensuel * 2 : ECONOMIE_ANNUELLE_CENTIMES)}{' '}
              d'économie
            </span>
          </button>
        </div>

        {/* Le passage de Solo à Duo.

            Un interrupteur, et non deux cartes de plus : Duo n'est pas un palier au-dessus
            de Solo, c'est Solo pris pour deux. Deux cartes côte à côte en feraient une
            offre « supérieure », donc Solo une offre diminuée — exactement ce que ce
            produit ne veut pas vendre à un parent qui n'a personne d'autre à couvrir.

            Il est proposé dans tous les cas, y compris quand l'autre parent n'a pas encore
            rejoint. Il l'était autrefois seulement à qui avait déjà un co-parent actif, ce
            qui le faisait disparaître au moment précis où l'on s'abonne — le plus souvent
            avant que l'invitation ne soit acceptée. La couverture se calcule à la lecture,
            côté serveur : la place s'ouvre d'elle-même le jour où l'autre rejoint, sans
            rien à refaire ici. Ce qui change selon les cas, c'est ce qu'on en dit. */}
        <div className="invitation-abonnement-duo">
          <label>
            <input
              type="checkbox"
              role="switch"
              checked={duo}
              onChange={(evenement) => setDuo(evenement.target.checked)}
            />
            <span>
              <strong>
                Duo — ouvrir aussi l'écriture à l'autre parent, {supplement.texte}
              </strong>
              {/* Ce que cette phrase doit désamorcer : l'idée qu'on achète un droit SUR
                  l'autre. Elle dit donc, dans l'ordre, ce qu'il ne saura pas, ce qu'il
                  garde, et ce qui se passe à l'arrêt. */}
              <span className="invitation-abonnement-duo-note">
                Il n'est pas informé de qui paie : il verra seulement que l'écriture est
                ouverte. Il peut prendre son propre abonnement quand il veut, et si le
                vôtre s'arrête, son accès va au bout de la période déjà payée.
              </span>
              {/* Dit seulement quand c'est vrai, et dit à l'avance : payer pour une place
                  vide sans le savoir serait une mauvaise surprise, et la découvrir après
                  coup ferait de Duo une option qu'on regrette. */}
              {placeEnAttente ? (
                <span className="invitation-abonnement-duo-note">
                  L'autre parent n'a pas encore rejoint le dossier : sa place l'attend, et
                  son écriture s'ouvrira le jour où il le rejoindra.
                </span>
              ) : null}
            </span>
          </label>
        </div>

        {/* Quand la première facture tombe. La question vient après « combien » et
            « pour qui », jamais avant : un essai mis en tête vendrait le gratuit plutôt
            que le produit, et c'est le prix qu'un parent veut connaître d'abord.

            Deux boutons plutôt qu'une case à cocher, et les deux issues écrites : « ne pas
            cocher » n'annonce pas ce qui se passe à la place, alors que « Payer tout de
            suite » le dit. Le libellé du bouton d'action suit ce choix — voir plus bas. */}
        <div
          className="invitation-abonnement-demarrage"
          role="radiogroup"
          aria-label="Démarrage"
        >
          <button
            type="button"
            role="radio"
            aria-checked={essai}
            className={essai ? 'actif' : undefined}
            onClick={() => setEssai(true)}
          >
            <span className="montant">{JOURS_ESSAI_GRATUIT} jours gratuits</span>
            <span className="detail">
              Puis {prixEnEuros(periodicite === 'yearly' ? prix.annuel : prix.mensuel)}{' '}
              {periodicite === 'yearly' ? 'par an' : 'par mois'}. Arrêté avant la fin des{' '}
              {JOURS_ESSAI_GRATUIT} jours, rien n'est débité.
            </span>
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={!essai}
            className={!essai ? 'actif' : undefined}
            onClick={() => setEssai(false)}
          >
            <span className="montant">Payer tout de suite</span>
            <span className="detail">
              L'abonnement démarre aujourd'hui, et l'écriture s'ouvre dans la foulée.
            </span>
          </button>
        </div>

        {/* Dit avant le paiement, parce que c'est la surprise classique d'un essai
            gratuit : la carte est demandée dans les deux cas. La taire ferait découvrir le
            formulaire de carte chez Stripe, après avoir cliqué sur « gratuit ». */}
        <p className="invitation-abonnement-carte discret">
          Votre carte est demandée dès maintenant dans les deux cas — c'est Stripe qui la
          recueille, jamais nous.
        </p>

        <ul className="invitation-abonnement-liste">
          {CE_QUE_CA_OUVRE.map((ligne) => (
            <li key={ligne}>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="m5 13 4 4L19 7" />
              </svg>
              <span>{ligne}</span>
            </li>
          ))}
        </ul>

        <p className="invitation-abonnement-gratuit">
          Le calendrier de garde et les alertes restent gratuits, avec ou sans abonnement.
          Prévenir l'autre parent qu'un enfant est malade ne dépend d'aucun paiement.
        </p>

        {/* Deux situations distinctes, et une seule phrase pour les deux mentirait à
            l'une des deux : le service fermé est un état du produit, l'échec technique est
            un incident qu'on peut retenter. Les deux disent d'abord ce qui n'a pas
            bougé — rien de débité, rien de perdu — avant de dire quoi faire. */}
        {ferme ? (
          <Alerte titre="La souscription n’est pas encore ouverte">
            Le paiement en ligne n’est pas encore en service. Vous n’avez rien à faire pour
            l’instant : tout ce que vous avez enregistré reste accessible, et la
            coordination avec l’autre parent continue de fonctionner.
          </Alerte>
        ) : souscrire.isError ? (
          // Deux échecs différents, et les confondre enverrait chercher au mauvais endroit :
          // l'un est un paiement qui ne s'ouvre pas, l'autre un message qui ne part pas.
          // Les deux disent d'abord ce qui n'a pas bougé.
          <Alerte
            titre={
              parEmail ? 'Le lien n’a pas pu être envoyé' : 'Le paiement n’a pas pu s’ouvrir'
            }
          >
            Vous n’avez pas été débité et votre dossier est intact. Réessayez dans un
            instant ; si le problème persiste, votre abonnement n’a pas démarré et vous
            pouvez continuer en version gratuite.
          </Alerte>
        ) : null}

        {/* Le lien est parti : on nomme l'adresse plutôt que de dire « votre boîte ». Une
            adresse mal saisie à l'inscription est la première cause d'un message qui
            n'arrive pas, et c'est le seul écran où le parent peut s'en apercevoir. */}
        {envoye ? (
          <Alerte titre="Le lien est parti">
            Nous venons d’envoyer votre lien de paiement à {profil?.email ?? 'votre adresse'}.
            Ouvrez-le pour terminer — ici, ou depuis un ordinateur. Il est valable une heure,
            et rien ne vous est débité tant que vous n’avez pas confirmé.
          </Alerte>
        ) : null}

        <div className="invitation-abonnement-actions">
          {/* Le bouton dit ce qui va se passer, essai compris. « S'abonner — 9,90 € / mois »
              posé sur un essai laisserait croire à un débit immédiat ; il porte donc le
              prix **et** ce qui le déclenche.

              Dans l'application, il annonce l'e-mail et **pas** le prix : ce qu'on appuie
              n'engage rien, et un montant sur un bouton qui n'ouvre pas de paiement se
              lirait comme un débit. Le prix, lui, est écrit au-dessus, sur la formule. */}
          {envoye ? null : (
            <Bouton onClick={() => souscrire.mutate()} disabled={souscrire.isPending}>
              {souscrire.isPending
                ? parEmail
                  ? 'Envoi…'
                  : 'Ouverture…'
                : parEmail
                  ? 'Recevoir le lien par e-mail'
                  : `${essai ? `Commencer l'essai — puis` : `S'abonner —`} ${
                      periodicite === 'yearly'
                        ? `${prixEnEuros(prix.annuel)} / an`
                        : `${prixEnEuros(prix.mensuel)} / mois`
                    }`}
            </Bouton>
          )}
          <Bouton variante="secondaire" onClick={() => dialogue.current?.close()}>
            {/* Après un refus d'écriture, « continuer en version gratuite » sonnerait comme
                un renoncement à ce qu'on venait de tenter ; ailleurs, c'est exactement le
                choix offert, et le nommer vaut mieux que de le repousser à plus tard.
                Le lien envoyé, il ne reste qu'à refermer : c'est la boîte aux lettres qui
                prend la suite. */}
            {envoye ? 'Fermer' : motif === 'refus' ? 'Plus tard' : 'Continuer en version gratuite'}
          </Bouton>
        </div>
      </div>
    </dialog>
  );
}
